const CARD_TYPES = new Set([
    'UNIT', 'SPELL', 'STRUCTURE', 'REACTION', 'VIRUS', 'EVENT', 'ITEM', 'EFFECT',
]);
const KIND_RANK = {
    unit: 0,
    card: 1,
    cell: 2,
    table: 3,
    player: 4,
    mode: 5,
};
function sideMatches(candidateSide, context, side) {
    if (side === undefined || side === 'any')
        return true;
    return side === 'ally' ? candidateSide === context.viewerSide : candidateSide !== context.viewerSide;
}
export function targetCandidateKey(candidate) {
    switch (candidate.kind) {
        case 'unit': return `unit:${candidate.instanceId}`;
        case 'card': return `card:${candidate.instanceId}:${candidate.zone}`;
        case 'cell': return `cell:${candidate.cellId}`;
        case 'table': return `table:${candidate.tableId}`;
        case 'player': return `player:${candidate.playerId}`;
        case 'mode': return `mode:${candidate.id}`;
    }
}
/** Enumerates one spec from public data only, in a deterministic canonical order. */
export function enumerateTargetCandidates(spec, context, previous = []) {
    assertTargetSpec(spec);
    switch (spec.kind) {
        case 'unit':
            return context.units
                .filter((unit) => sideMatches(unit.side, context, spec.side))
                // A registry-free client context may omit cardType; complete actions
                // remain authoritative and filter the visible superset by candidate key.
                .filter((unit) => spec.cardTypes === undefined || unit.cardType === undefined
                || spec.cardTypes.includes(unit.cardType))
                .filter((unit) => spec.where?.({ unit, context, previous }) ?? true)
                .map((unit) => ({ kind: 'unit', instanceId: unit.instanceId }));
        case 'card':
            return context.cards
                .filter((card) => spec.zones.includes(card.zone))
                .filter((card) => {
                const owner = context.players.find((player) => player.playerId === card.ownerId);
                return owner !== undefined && sideMatches(owner.side, context, spec.side);
            })
                .filter((card) => spec.where?.({ card, context, previous }) ?? true)
                .map((card) => ({ kind: 'card', instanceId: card.instanceId, zone: card.zone }));
        case 'cell':
            return context.cells
                .filter((cell) => sideMatches(cell.side, context, spec.side))
                .filter((cell) => spec.occupancy === undefined || spec.occupancy === 'any'
                || (spec.occupancy === 'empty' ? cell.occupantId === null : cell.occupantId !== null))
                .filter((cell) => spec.where?.({ cell, context, previous }) ?? true)
                .map((cell) => ({ kind: 'cell', cellId: cell.cellId }));
        case 'table':
            return context.tables
                .filter((table) => spec.where?.({ table, context, previous }) ?? true)
                .map((table) => ({ kind: 'table', tableId: table.tableId }));
        case 'player':
            return context.players
                .filter((player) => sideMatches(player.side, context, spec.side))
                .filter((player) => spec.where?.({ player, context, previous }) ?? true)
                .map((player) => ({ kind: 'player', playerId: player.playerId }));
        case 'mode':
            return spec.options.map((option) => ({ kind: 'mode', id: option.id }));
    }
}
export function assertTargetSpec(spec) {
    if (typeof spec !== 'object' || spec === null)
        throw new Error('TargetSpec должен быть объектом');
    const { min, max } = spec.cardinality;
    if (!Number.isInteger(min) || !Number.isInteger(max) || min < 0 || max < min) {
        throw new Error(`TargetSpec.cardinality требует целые 0 <= min <= max (получено ${min}..${max})`);
    }
    if (spec.onInsufficient !== undefined && !['skip', 'fizzle', 'match_error'].includes(spec.onInsufficient)) {
        throw new Error(`TargetSpec.onInsufficient "${String(spec.onInsufficient)}" неизвестен`);
    }
    if (spec.kind === 'unit' && spec.cardTypes !== undefined) {
        if (!Array.isArray(spec.cardTypes) || spec.cardTypes.length === 0
            || new Set(spec.cardTypes).size !== spec.cardTypes.length
            || spec.cardTypes.some((type) => !CARD_TYPES.has(type))) {
            throw new Error('UnitTargetSpec.cardTypes должен содержать уникальные известные типы карт');
        }
    }
    if (spec.kind === 'card') {
        if (!Array.isArray(spec.zones) || spec.zones.length === 0 || new Set(spec.zones).size !== spec.zones.length) {
            throw new Error('CardTargetSpec.zones должен содержать уникальные зоны');
        }
        if (spec.zones.includes('deck') || spec.zones.includes('armed')) {
            throw new Error('CardTargetSpec не допускает exact targeting в deck/armed');
        }
    }
    if (spec.kind === 'mode') {
        const ids = spec.options.map((option) => option.id);
        if (ids.length === 0 || ids.some((id) => typeof id !== 'string' || id === '') || new Set(ids).size !== ids.length) {
            throw new Error('ModeTargetSpec.options требует уникальные непустые id');
        }
    }
}
export function validateAndCanonicalizeTargetSelection(spec, domain, selection) {
    if (!Array.isArray(selection))
        throw new Error('TargetSelection должен быть массивом');
    const { min, max } = spec.cardinality;
    if (selection.length < min || selection.length > max) {
        throw new Error(`выбрано ${selection.length} целей, допустимо ${min}..${max}`);
    }
    const domainByKey = new Map(domain.map((candidate) => [targetCandidateKey(candidate), candidate]));
    const pickedKeys = selection.map(targetCandidateKey);
    if (new Set(pickedKeys).size !== pickedKeys.length)
        throw new Error('повтор одной цели запрещён');
    const picked = pickedKeys.map((key) => {
        const candidate = domainByKey.get(key);
        if (candidate === undefined)
            throw new Error(`цель "${key}" отсутствует в materialized domain`);
        return candidate;
    });
    if (spec.ordered === true)
        return picked;
    const rank = new Map(domain.map((candidate, index) => [targetCandidateKey(candidate), index]));
    return picked.sort((a, b) => rank.get(targetCandidateKey(a)) - rank.get(targetCandidateKey(b)));
}
/** Stable fallback comparator for already-public candidates of mixed kinds. */
export function compareTargetCandidates(a, b) {
    if (a.kind !== b.kind)
        return KIND_RANK[a.kind] - KIND_RANK[b.kind];
    return targetCandidateKey(a).localeCompare(targetCandidateKey(b));
}
export function enumerateTargetSelections(spec, domain) {
    assertTargetSpec(spec);
    const out = [];
    const max = Math.min(spec.cardinality.max, domain.length);
    const visit = (prefix, remaining, size) => {
        if (prefix.length === size) {
            out.push([...prefix]);
            return;
        }
        for (let index = 0; index < remaining.length; index += 1) {
            const candidate = remaining[index];
            const nextRemaining = spec.ordered === true
                ? remaining.filter((_, remainingIndex) => remainingIndex !== index)
                : remaining.slice(index + 1);
            visit([...prefix, candidate], nextRemaining, size);
        }
    };
    for (let size = spec.cardinality.min; size <= max; size += 1)
        visit([], [...domain], size);
    return out;
}
//# sourceMappingURL=enumerate.js.map